import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  CheckCircle, 
  XCircle, 
  Clock, 
  Package, 
  Truck, 
  AlertCircle,
  ArrowLeft,
  RefreshCw,
  Phone,
  Mail,
  Printer
} from 'lucide-react';
import toast from 'react-hot-toast';
import cinetpayService from '../services/cinetpayService';
import firebaseService from '../services/firebaseService';
import '../styles/PaymentStatus.css';

export default function PaymentStatus() {
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState('loading');
  const [orderDetails, setOrderDetails] = useState(null);
  const [checking, setChecking] = useState(false);
  const [verificationCount, setVerificationCount] = useState(0);

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const transactionId = queryParams.get('transaction_id');
    const statusParam = queryParams.get('status');

    console.log('📱 Page de statut - Transaction:', transactionId, 'Status:', statusParam);

    if (transactionId) {
      verifyPaymentStatus(transactionId);
    } else {
      setStatus('error');
    }
  }, [location]);

  const verifyPaymentStatus = async (transactionId, isRetry = false) => {
    setChecking(true);
    
    try {
      console.log(`🔍 Vérification du statut pour ${transactionId}...`);
      
      // 1. Vérifier via l'API CinetPay
      const statusResult = await cinetpayService.checkPaymentStatus(transactionId);
      
      console.log('📊 Résultat CinetPay:', statusResult);

      // 2. Récupérer les détails de la commande en local
      const orderKey = `order_${transactionId}`;
      const savedOrder = sessionStorage.getItem(orderKey);
      let order = savedOrder ? JSON.parse(savedOrder) : null;

      // 3. Déterminer le statut final
      let finalStatus = statusResult.status;
      
      if (statusResult.success) {
        finalStatus = 'SUCCESS';
        toast.success('✅ Paiement confirmé !');
      } else if (statusResult.status === 'FAILED') {
        finalStatus = 'FAILED';
        toast.error('❌ Paiement échoué');
      } else if (statusResult.status === 'PENDING') {
        // Si c'est toujours en attente après plusieurs vérifications
        if (verificationCount > 2) {
          finalStatus = 'PENDING';
          toast.loading('Paiement en cours de traitement...');
        } else {
          // Réessayer dans 3 secondes
          setVerificationCount(prev => prev + 1);
          setTimeout(() => verifyPaymentStatus(transactionId, true), 3000);
          return;
        }
      }

      // 4. METTRE À JOUR FIREBASE
      console.log(`🔄 Mise à jour Firebase: ${finalStatus}`);
      
      const firebaseUpdate = await firebaseService.updateVenteStatus(
        transactionId, 
        finalStatus,
        {
          reference: statusResult.data?.payment_method,
          mode: statusResult.data?.payment_method,
          date: new Date().toISOString()
        }
      );

      if (firebaseUpdate.success) {
        console.log('✅ Firebase mis à jour:', firebaseUpdate);
      } else {
        console.warn('⚠️ Firebase non mis à jour:', firebaseUpdate);
      }

      // 5. Mettre à jour le sessionStorage
      if (order) {
        order.status = finalStatus;
        order.verifiedAt = new Date().toISOString();
        if (finalStatus === 'SUCCESS') {
          order.paymentDate = new Date().toISOString();
        }
        sessionStorage.setItem(orderKey, JSON.stringify(order));
        setOrderDetails(order);
      }

      // 6. Mettre à jour l'état local
      setStatus(finalStatus.toLowerCase());

    } catch (error) {
      console.error('❌ Erreur vérification:', error);
      
      // En cas d'erreur, essayer de récupérer le statut depuis CinetPay directement
      if (verificationCount < 3) {
        setVerificationCount(prev => prev + 1);
        setTimeout(() => verifyPaymentStatus(transactionId, true), 3000);
      } else {
        setStatus('error');
        toast.error('Impossible de vérifier le statut du paiement');
      }
    } finally {
      setChecking(false);
    }
  };

  const handleRetry = () => {
    const transactionId = new URLSearchParams(location.search).get('transaction_id');
    if (transactionId) {
      setVerificationCount(0);
      verifyPaymentStatus(transactionId);
    }
  };

  const handleNewOrder = () => {
    sessionStorage.removeItem('ultimatag_delivery_info');
    sessionStorage.removeItem('current_transaction_id');
    navigate('/');
  };

  const handlePrint = () => {
    window.print();
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-CI', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(price);
  };

  const getStatusConfig = () => {
    switch(status) {
      case 'success':
        return {
          icon: <CheckCircle size={64} className="status-icon success" />,
          title: 'Paiement réussi !',
          subtitle: 'Votre commande a été confirmée',
          color: '#10b981'
        };
      case 'failed':
        return {
          icon: <XCircle size={64} className="status-icon failed" />,
          title: 'Paiement échoué',
          subtitle: 'Le paiement n\'a pas pu être effectué',
          color: '#ef4444'
        };
      case 'pending':
        return {
          icon: <Clock size={64} className="status-icon pending" />,
          title: 'Paiement en cours',
          subtitle: 'Votre paiement est en cours de traitement',
          color: '#f59e0b'
        };
      default:
        return {
          icon: <AlertCircle size={64} className="status-icon error" />,
          title: 'Erreur',
          subtitle: 'Une erreur est survenue',
          color: '#6b7280'
        };
    }
  };

  const config = getStatusConfig();

  if (status === 'loading') {
    return (
      <div className="payment-status-container">
        <div className="payment-status-loading">
          <div className="spinner-large"></div>
          <h2>Vérification de votre paiement...</h2>
          <p>Veuillez patienter pendant que nous confirmons votre transaction.</p>
          {verificationCount > 0 && (
            <p className="verification-attempt">
              Tentative {verificationCount}/3...
            </p>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="payment-status-container">
      <div className="payment-status-card">
        {/* En-tête avec statut */}
        <div className="status-header" style={{ backgroundColor: `${config.color}10` }}>
          <div className="status-icon-wrapper" style={{ color: config.color }}>
            {config.icon}
          </div>
          <h1 className="status-title" style={{ color: config.color }}>
            {config.title}
          </h1>
          <p className="status-subtitle">{config.subtitle}</p>
        </div>

        {/* Détails de la commande */}
        {orderDetails && (
          <div className="order-details">
            <h2 className="order-details-title">
              <Package size={20} />
              Détails de votre commande
            </h2>

            <div className="order-details-grid">
              <div className="order-detail-item">
                <span className="order-detail-label">Numéro de commande</span>
                <span className="order-detail-value">#{orderDetails.transactionId}</span>
              </div>
              
              <div className="order-detail-item">
                <span className="order-detail-label">Pack commandé</span>
                <span className="order-detail-value">{orderDetails.pack}</span>
              </div>
              
              <div className="order-detail-item">
                <span className="order-detail-label">Montant total</span>
                <span className="order-detail-value highlight">
                  {formatPrice(orderDetails.amount)}
                </span>
              </div>

              {orderDetails.customer && (
                <>
                  <div className="order-detail-item full-width">
                    <span className="order-detail-label">Client</span>
                    <span className="order-detail-value">
                      {orderDetails.customer.name}
                    </span>
                  </div>

                  <div className="order-detail-item full-width">
                    <span className="order-detail-label">Adresse de livraison</span>
                    <span className="order-detail-value">
                      {orderDetails.customer.address}, {orderDetails.customer.zone}, {orderDetails.customer.city}
                    </span>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* Actions selon le statut */}
        <div className="status-actions">
          {status === 'success' && (
            <>
              <button onClick={handlePrint} className="status-button print">
                <Printer size={18} />
                Imprimer le récépissé
              </button>
              <button onClick={handleNewOrder} className="status-button primary">
                <Package size={18} />
                Commander à nouveau
              </button>
            </>
          )}

          {status === 'failed' && (
            <>
              <button onClick={handleRetry} className="status-button primary" disabled={checking}>
                {checking ? (
                  <>
                    <RefreshCw size={18} className="spinning" />
                    Vérification...
                  </>
                ) : (
                  <>
                    <RefreshCw size={18} />
                    Vérifier à nouveau
                  </>
                )}
              </button>
              <button onClick={handleNewOrder} className="status-button secondary">
                <Package size={18} />
                Nouvelle commande
              </button>
            </>
          )}

          {status === 'pending' && (
            <>
              <button onClick={handleRetry} className="status-button primary" disabled={checking}>
                {checking ? (
                  <>
                    <RefreshCw size={18} className="spinning" />
                    Vérification...
                  </>
                ) : (
                  <>
                    <RefreshCw size={18} />
                    Vérifier le statut
                  </>
                )}
              </button>
              <button onClick={handleNewOrder} className="status-button secondary">
                <Package size={18} />
                Retour à l'accueil
              </button>
            </>
          )}

          {status === 'error' && (
            <button onClick={() => navigate('/')} className="status-button primary">
              <ArrowLeft size={18} />
              Retour à l'accueil
            </button>
          )}
        </div>

        {/* Support */}
        <div className="status-support">
          <h3>Besoin d'aide ?</h3>
          <div className="support-contact">
            <div className="support-item">
              <Phone size={16} />
              <span>+225 27 22 22 22 22</span>
            </div>
            <div className="support-item">
              <Mail size={16} />
              <span>support@ultimatag.com</span>
            </div>
          </div>
          <p className="support-note">
            Notre équipe est disponible du lundi au vendredi de 8h à 18h.
          </p>
        </div>

        {/* Note de vérification */}
        {status === 'pending' && (
          <div className="verification-note">
            <AlertCircle size={16} />
            <p>
              Le paiement peut prendre quelques minutes à être confirmé. 
              Vous pouvez vérifier manuellement en cliquant sur "Vérifier le statut".
            </p>
          </div>
        )}
      </div>
    </div>
  );
}